import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import TmcIcon from '@/components/tmc/TmcIcon'

type MessageType = 'error' | 'success' | 'warning' | 'info'

interface VariantStyle {
  wrapper: string
  closeColor: string
  icon: string
}

const variantStyles: Record<MessageType, VariantStyle> = {
  error: { wrapper: 'bg-purple-highlight border-[1.5px] border-purple-shade text-text', closeColor: 'text-text', icon: '59-cirkel-info' },
  success: { wrapper: 'bg-mint-highlight border border-mint text-text', closeColor: 'text-text', icon: '28-cirkel-vinkje' },
  warning: { wrapper: 'bg-beige-shade border border-beige-shade text-text', closeColor: 'text-text', icon: '59-cirkel-info' },
  info: { wrapper: 'bg-purple-highlight border-[1.5px] border-purple-shade text-text', closeColor: 'text-text', icon: '59-cirkel-info' },
}

interface FloatingMessageBoxProps {
  type?: MessageType
  message: string
  onClose?: () => void
  duration?: number
  icon?: React.ReactNode
}

const FloatingMessageBox = ({ type = 'error', message, onClose, duration, icon }: FloatingMessageBoxProps): React.ReactElement | null => {
  const [leaving, setLeaving] = useState<boolean>(false)

  const close = (): void => {
    setLeaving(true)
    // wacht op de slide-up animatie (0.4s) voor we unmounten
    if (onClose) setTimeout(onClose, 400)
  }

  useEffect(() => {
    // Errors blijven staan tot de patiënt ze zelf sluit - vanzelf verdwijnen maakte de melding te vluchtig.
    if (!message || !duration || type === 'error') return
    const timer = setTimeout(() => {
      setLeaving(true)
      if (onClose) setTimeout(onClose, 400)
    }, duration)
    return () => clearTimeout(timer)
  }, [message, duration, type, onClose])

  if (!message) return null

  const variant = variantStyles[type] || variantStyles.error

  return (
    <div
      className={clsx(
        'fixed left-0 right-0 top-0 z-[9999] flex justify-center px-4 pt-[calc(env(safe-area-inset-top)+12px)] will-change-transform',
        leaving ? 'animate-[banner-up_0.4s_ease_forwards]' : 'animate-[banner-down_0.4s_ease]'
      )}
    >
      <div
        data-testid="app-toast"
        data-message-type={type}
        className={clsx(
          'flex items-start gap-2 w-full max-w-md box-border rounded-lg px-4 py-3 pr-10 text-[0.95rem] font-medium relative shadow-md',
          variant.wrapper
        )}
      >
        <span className="shrink-0 mt-0.5">{icon ?? <TmcIcon name={variant.icon} size={20} />}</span>
        <span className="flex-1 text-left">{message}</span>
        <button
          type="button"
          onClick={close}
          className={clsx(
            'absolute right-3 top-3 bg-transparent border-0 text-[1.2rem] cursor-pointer leading-none p-0',
            variant.closeColor
          )}
        >
          &times;
        </button>
      </div>
    </div>
  )
}

export default FloatingMessageBox
